import type { ClefName } from './clefs';
import type { NoteName } from './notesAndAccidentals';

export const keyQualities = ['Major', 'minor'] as const;
export type KeyQuality = typeof keyQualities[number];

export function isKeyQuality(quality: string): quality is KeyQuality {
  return keyQualities.includes(quality as KeyQuality);
}

/**
 * Order of sharps
 */
export const keySignatureSharps = ['F#', 'C#', 'G#', 'D#', 'A#', 'E#', 'B#'] as const;

/**
 * Order of flats
 */
export const keySignatureFlats = ['Bb', 'Eb', 'Ab', 'Db', 'Gb', 'Cb', 'Fb'] as const;

/**
 * Sharp major keys from 0 sharps to 7 sharps
 */
export const sharpMajors = ['C', 'G', 'D', 'A', 'E', 'B', 'F#', 'C#'] as const;

/**
 * Flat major keys from 0 flats to 7 flats
 */
export const flatMajors = ['C', 'F', 'Bb', 'Eb', 'Ab', 'Db', 'Gb', 'Cb'] as const;

/**
 * Sharp minor keys from 0 sharps to 7 sharps
 */
export const sharpMinors = ['A', 'E', 'B', 'F#', 'C#', 'G#', 'D#', 'A#'] as const;

/**
 * Flat minor keys from 0 flats to 7 flats
 */
export const flatMinors = ['A', 'D', 'G', 'C', 'F', 'Bb', 'Eb', 'Ab'] as const;

/**
 * Dictionary of notes with octaves for key signatures by clef.
 */
export const keySignatureClefNotes: {
  sharps: Record<ClefName, NoteName[]>;
  flats: Record<ClefName, NoteName[]>;
} = {
  sharps: {
    treble: ['F#5', 'C#5', 'G#5', 'D#5', 'A#4', 'E#5', 'B#4'],
    bass: ['F#3', 'C#3', 'G#3', 'D#3', 'A#2', 'E#3', 'B#2'],
    soprano: ['F#4', 'C#4', 'G#4', 'D#4', 'A#4', 'E#4', 'B#4'],
    'mezzo-soprano': ['F#4', 'C#4', 'G#4', 'D#4', 'A#4', 'E#4', 'B#4'],
    alto: ['F#4', 'C#4', 'G#4', 'D#4', 'A#3', 'E#4', 'B#3'],
    tenor: ['F#3', 'C#4', 'G#3', 'D#4', 'A#3', 'E#4', 'B#3'],
    baritone: ['F#3', 'C#3', 'G#3', 'D#3', 'A#3', 'E#3', 'B#3'],
    percussion: ['F#5', 'C#5', 'G#5', 'D#5', 'A#4', 'E#5', 'B#4'],
  },
  flats: {
    treble: ['Bb4', 'Eb5', 'Ab4', 'Db5', 'Gb4', 'Cb5', 'Fb4'],
    bass: ['Bb2', 'Eb3', 'Ab2', 'Db3', 'Gb2', 'Cb3', 'Fb2'],
    soprano: ['Bb4', 'Eb4', 'Ab4', 'Db4', 'Gb4', 'Cb4', 'Fb4'],
    'mezzo-soprano': ['Bb4', 'Eb4', 'Ab4', 'Db4', 'Gb4', 'Cb4', 'Fb4'],
    alto: ['Bb3', 'Eb4', 'Ab3', 'Db4', 'Gb3', 'Cb4', 'Fb3'],
    tenor: ['Bb3', 'Eb4', 'Ab3', 'Db4', 'Gb3', 'Cb4', 'Fb3'],
    baritone: ['Bb3', 'Eb3', 'Ab3', 'Db3', 'Gb3', 'Cb3', 'Fb3'],
    percussion: ['Bb4', 'Eb5', 'Ab4', 'Db5', 'Gb4', 'Cb5', 'Fb4'],
  },
};
